/**
 * Folder tree rendering — extracted from SharedFilesView. Flattens the
 * folder hierarchy into a single array (via `flattenTree`) so the JSX
 * itself contains ZERO recursion. babel-parser choked on the recursive
 * component variant, so we hoist the recursion into plain JS.
 */
import { useState } from "react";
import { Folder, FolderPlus, ChevronRight, ChevronDown, Pencil, Trash2 } from "lucide-react";

/** MIME the shared-files drag payload uses. Kept in sync with SharedFileCard. */
const DRAG_MIME = "application/serpix-file-ids";

function parseDropIds(e) {
  const raw = e.dataTransfer.getData(DRAG_MIME);
  if (!raw) return [];
  try {
    const ids = JSON.parse(raw);
    return Array.isArray(ids) ? ids.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function useDropTarget(onDropFiles) {
  const [over, setOver] = useState(false);
  if (!onDropFiles) return { over: false, handlers: {} };
  return {
    over,
    handlers: {
      onDragOver: (e) => {
        if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
        e.preventDefault();
        setOver(true);
      },
      onDragLeave: () => setOver(false),
      onDrop: (e) => {
        setOver(false);
        const ids = parseDropIds(e);
        if (ids.length) {
          e.preventDefault();
          onDropFiles(ids);
        }
      },
    },
  };
}

export function FolderRow({ active, onClick, Icon, label, testid, onDropFiles }) {
  const { over, handlers } = useDropTarget(onDropFiles);
  const rowClass = over
    ? "group flex items-center gap-1 rounded-md bg-violet-100 px-2 py-1.5 text-sm text-violet-900 ring-2 ring-violet-400"
    : active
      ? "group flex items-center gap-1 rounded-md bg-zinc-900 px-2 py-1.5 text-sm text-white"
      : "group flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100";
  return (
    <li>
      <div {...handlers} className={rowClass}>
        <button
          type="button"
          onClick={onClick}
          data-testid={testid}
          className="flex flex-1 items-center gap-1.5 truncate text-left"
        >
          <Icon className={active ? "h-3.5 w-3.5" : "h-3.5 w-3.5 text-zinc-400"} />
          <span className="truncate">{label}</span>
        </button>
      </div>
    </li>
  );
}

/**
 * Recursively walk the tree and produce a flat list of `{folder, depth}`
 * entries in visible order. Collapsed sub-trees are skipped.
 */
export function flattenTree(rootFolders, expanded, childrenOf) {
  const out = [];
  const walk = (folder, depth) => {
    out.push({ folder, depth });
    if (!expanded.has(folder.id)) return;
    const kids = childrenOf(folder.id);
    for (const k of kids) walk(k, depth + 1);
  };
  for (const r of rootFolders) walk(r, 0);
  return out;
}

/**
 * Single row in the flattened list. No recursion in JSX — the parent
 * SharedFilesView just maps over the flat list produced by flattenTree.
 */
export function FolderTreeItem({
  folder,
  depth,
  current,
  expanded,
  onToggle,
  onSelect,
  hasChildren,
  onAddChild,
  onRename,
  onDelete,
  onDropFiles,
}) {
  const isOpen = expanded.has(folder.id);
  const active = current === folder.id;
  const count = folder.file_count || 0;
  const { over, handlers } = useDropTarget(
    onDropFiles ? (ids) => onDropFiles(ids, folder) : null,
  );
  const rowClass = over
    ? "group flex items-center gap-1 rounded-md bg-violet-100 px-1 py-1.5 text-sm text-violet-900 ring-2 ring-violet-400"
    : active
      ? "group flex items-center gap-1 rounded-md bg-zinc-900 px-1 py-1.5 text-sm text-white"
      : "group flex items-center gap-1 rounded-md px-1 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100";
  return (
    <li>
      <div
        {...handlers}
        className={rowClass}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(folder.id);
            }}
            className={
              active
                ? "text-white/70 hover:text-white"
                : "text-zinc-400 hover:text-zinc-900"
            }
          >
            {isOpen ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        ) : (
          <span className="w-3" />
        )}
        <button
          type="button"
          onClick={() => onSelect(folder.id)}
          data-testid={`shared-folder-${folder.id}`}
          className="flex flex-1 items-center gap-1.5 truncate text-left"
        >
          <Folder
            className={active ? "h-3.5 w-3.5" : "h-3.5 w-3.5 text-amber-500"}
          />
          <span className="truncate">{folder.name}</span>
        </button>
        {count > 0 && (
          <span
            data-testid={`shared-folder-count-${folder.id}`}
            title={`${count} dosya (alt klasörler dahil)`}
            className={
              active
                ? "ml-1 inline-flex h-4 min-w-[18px] items-center justify-center rounded-full bg-white/20 px-1.5 text-[10px] font-semibold text-white group-hover:hidden"
                : "ml-1 inline-flex h-4 min-w-[18px] items-center justify-center rounded-full bg-zinc-200 px-1.5 text-[10px] font-semibold text-zinc-700 group-hover:hidden"
            }
          >
            {count}
          </span>
        )}
        {(onAddChild || onRename || onDelete) && (
          // Action affordances are always visible (muted on rest state,
          // full opacity on hover/focus/active) so elevated users see
          // the edit + delete controls without hunting for them. Only
          // rendered when at least one action is wired in from the
          // parent — respects canMutate gating.
          <div className={`ml-auto flex gap-0.5 opacity-70 group-hover:opacity-100 ${active ? "opacity-100" : ""}`}>
            {onAddChild ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddChild(folder);
                }}
                className={
                  active
                    ? "rounded p-0.5 text-white/70 hover:bg-white/10"
                    : "rounded p-0.5 text-zinc-400 hover:bg-blue-100 hover:text-blue-600"
                }
                title="Alt klasör oluştur"
                data-testid={`shared-folder-add-child-${folder.id}`}
              >
                <FolderPlus className="h-3 w-3" />
              </button>
            ) : null}
            {onRename ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRename(folder);
                }}
                className={
                  active
                    ? "rounded p-0.5 text-white/70 hover:bg-white/10"
                    : "rounded p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-900"
                }
                title="Yeniden adlandır"
                data-testid={`shared-folder-rename-${folder.id}`}
              >
                <Pencil className="h-3 w-3" />
              </button>
            ) : null}
            {onDelete ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(folder);
                }}
                className={
                  active
                    ? "rounded p-0.5 text-white/70 hover:bg-white/10"
                    : "rounded p-0.5 text-zinc-400 hover:bg-red-100 hover:text-red-600"
                }
                title="Sil"
                data-testid={`shared-folder-delete-${folder.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        )}
      </div>
    </li>
  );
}
